"use client";

import { Card, CardContent, CardHeader, CardTitle } from "ui/components/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "ui/components/tooltip";
import { formatHoursToTime } from "components/timesheet/utils";
import { cn } from "utils";
import { useMemo } from "react";

const ProgressBar = ({ value, maxValue, color, height, className }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);

  return (
    <div
      className={cn(
        "absolute inset-0 rounded-sm transition-all duration-300 my-auto opacity-80",
        color,
        height,
        className
      )}
      style={{
        width: `${percentage}%`,
      }}
    />
  );
};

const AxisGrid = ({ maxValue }) => {
  const steps = 5;
  const values = Array.from({ length: steps + 1 }, (_, i) =>
    Math.round((maxValue / steps) * i)
  );

  return (
    <div className="absolute inset-0 pointer-events-none">
      {values.map((value, index) => (
        <div
          key={index}
          className="absolute top-0 bottom-0"
          style={{ left: `${(index * 100) / steps}%` }}
        >
          {/* Top label */}
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-full mb-1 mx-2">
            <span className="text-xs text-muted-foreground">{value}h</span>
          </div>
          {/* Vertical grid line */}
          <div className="h-full w-px bg-border opacity-50" />
        </div>
      ))}
    </div>
  );
};

const BarGroup = ({ job, maxValue }) => {
  const { spent_hours = 0, consumed_hours = 0, allocated_hours = 0 } = job;

  const hasData = spent_hours > 0 || consumed_hours > 0 || allocated_hours > 0;
  if (!hasData) return null;

  return (
    <div className="relative flex items-center">
      <div className="flex-1 relative h-16">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="hover:bg-muted/50 w-full h-16 cursor-pointer rounded-sm">
                {allocated_hours > 0 && (
                  <div className="absolute inset-0 z-10">
                    <ProgressBar
                      value={allocated_hours}
                      maxValue={maxValue}
                      color="bg-slate-300 dark:bg-slate-600"
                      height="h-10"
                    />
                  </div>
                )}

                {spent_hours > 0 && (
                  <div className="absolute inset-0 z-20">
                    <ProgressBar
                      value={spent_hours}
                      maxValue={maxValue}
                      color="bg-sky-100 dark:bg-sky-600"
                      height="h-6"
                    />
                  </div>
                )}

                {consumed_hours > 0 && (
                  <div className="absolute inset-0 z-30">
                    <ProgressBar
                      value={consumed_hours}
                      maxValue={maxValue}
                      color="bg-teal-700 dark:bg-teal-600"
                      height="h-4"
                    />
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <p>Allocated: {formatHoursToTime(allocated_hours)}</p>
                <p>Consumed: {formatHoursToTime(consumed_hours)}</p>
                <p>Spent: {formatHoursToTime(spent_hours)}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

const ChartLegend = ({ job, showLabel, index, showAllocatedHours = true }) => {
  const { name } = job;

  const legendItems = [
  ...(showAllocatedHours ? [
      {
      color: "bg-slate-300 dark:bg-slate-600",
      label: "Allocated Hours",
    }]: []),
    {
      color: "bg-sky-100 dark:bg-sky-600",
      label: "Spent Hours",
    },
    {
      color: "bg-teal-700 dark:bg-teal-600",
      label: "Consumed Hours",
    },
  ];

  return (
    <div className="flex flex-wrap justify-between items-center gap-2">
      {showLabel && (
        <div className="flex-1 min-w-0">
          <div className="text-base font-normal text-gray-600 truncate">
            {name}
          </div>
        </div>
      )}
      {index === 0 && (
        <div className="flex flex-wrap gap-4 justify-end">
          {legendItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className={cn("w-3 h-3 rounded", item.color)} />
              <span className="text-xs text-muted-foreground">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function NestedProgressChart({
  data,
  title = "Burn Chart",
  showLabel = false,
  info,
  className,
  index,
  showAllocatedHours = true
}) {

  const maxValue = useMemo(() => {
    const max = Math.max(
      ...data.flatMap((item) => [
        ...(showAllocatedHours ? [item.allocated_hours ?? 0] : []),
        item.consumed_hours ?? 0,
        item.spent_hours ?? 0,
      ])
    );
    return Math.ceil(max / 5) * 5;
  }, [data]);

  return (
    <div className={cn("w-full", className)}>
      {data.map((job) => {
        const hasData =
          (showAllocatedHours && (job.allocated_hours ?? 0)) > 0 ||
          (job.consumed_hours ?? 0) > 0 ||
          (job.spent_hours ?? 0) > 0;

        return (
          <div key={job.id} className="flex flex-col gap-9">
            <ChartLegend job={job} showLabel={showLabel} index={index} showAllocatedHours={showAllocatedHours}/>
            {hasData ? (
              <div className="relative">
                <AxisGrid maxValue={maxValue} />
                <BarGroup job={job} maxValue={maxValue} />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground h-16 flex items-center justify-center border rounded-md bg-muted/40">
                No data available
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
